import { useEffect, useMemo, useState } from "react";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Btn } from "../components/Btn";
import { Eyebrow } from "../components/Eyebrow";
import { Icon } from "../components/Icon";
import { EmptyState } from "../components/EmptyState";
import { useCart } from "../store/cartStore";
import { supabase } from "../lib/supabase";
import { gradientForId } from "../lib/gradients";
import { fmtK } from "../lib/money";

// Cart — all items from localStorage-persisted cartStore.
// Delivery fees resolved live from the sellers table so they always match the
// storefront; if a seller has no recorded fee we fall back to 0.
export function Cart({ setActive, onCheckout }) {
  const items = useCart((s) => s.items);
  const bump = useCart((s) => s.bump);
  const removeItem = useCart((s) => s.remove);
  const clear = useCart((s) => s.clear);
  const subtotalNgwee = useCart((s) => s.subtotalNgwee());

  const sellerIds = useMemo(() => [...new Set(items.map((i) => i.seller_id).filter(Boolean))], [items]);
  const [sellerMeta, setSellerMeta] = useState({});

  useEffect(() => {
    if (!sellerIds.length) return;
    let cancelled = false;
    supabase
      .from("sellers")
      .select("id, store_name, delivery_fee, rating_avg, address, logo_url")
      .in("id", sellerIds)
      .then(({ data }) => {
        if (cancelled || !data) return;
        const map = {};
        for (const s of data) map[s.id] = s;
        setSellerMeta(map);
      });
    return () => {
      cancelled = true;
    };
  }, [sellerIds.join(",")]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const i of items) {
      const key = i.seller_id || "unknown";
      if (!map.has(key)) map.set(key, { seller_id: i.seller_id, seller_name: i.seller_name, items: [] });
      map.get(key).items.push(i);
    }
    return Array.from(map.values());
  }, [items]);

  const deliveryNgwee = useMemo(
    () => groups.reduce((s, g) => s + (sellerMeta[g.seller_id]?.delivery_fee || 0), 0),
    [groups, sellerMeta]
  );
  const totalNgwee = subtotalNgwee + deliveryNgwee;
  const itemCount = items.reduce((s, i) => s + (i.qty || 0), 0);

  if (!items.length) {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <Eyebrow>Cart</Eyebrow>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0", letterSpacing: "-0.6px" }}>Your cart</h1>
        </div>
        <EmptyState
          icon="shopping-cart"
          title="Your cart is empty"
          body="Browse the marketplace and add items — they'll stay here until you check out."
          action={
            <Btn icon="compass" onClick={() => setActive?.("discover")}>
              Discover items
            </Btn>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 18 }}>
        <div>
          <Eyebrow>
            Cart · {groups.length} seller{groups.length === 1 ? "" : "s"}
          </Eyebrow>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0", letterSpacing: "-0.6px" }}>Your cart</h1>
        </div>
        <Btn kind="ghost" size="sm" icon="trash-2" onClick={clear}>
          Clear all
        </Btn>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 18, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {groups.map((g) => (
            <SellerGroup
              key={g.seller_id || "unknown"}
              group={g}
              seller={sellerMeta[g.seller_id]}
              onBump={(key, d) => bump(key, d)}
              onRemove={(key) => removeItem(key)}
            />
          ))}
        </div>

        <div style={{ position: "sticky", top: 82, display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <Eyebrow>Order summary</Eyebrow>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13, margin: "10px 0 12px" }}>
              <Row label={`Subtotal (${itemCount} item${itemCount === 1 ? "" : "s"})`} value={fmtK(subtotalNgwee)} />
              <Row label="Delivery" value={deliveryNgwee > 0 ? fmtK(deliveryNgwee) : "Free"} />
              <Row label="Platform fee" value="Free" valueColor={C.greenDk} />
              <div style={{ height: 1, background: "rgba(196,193,183,0.4)", margin: "4px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ fontWeight: 800, fontSize: 14 }}>Total</span>
                <span style={{ fontSize: 22, fontWeight: 900, color: C.hunter, letterSpacing: "-0.4px" }}>
                  {fmtK(totalNgwee)}
                </span>
              </div>
            </div>
            <Btn full size="lg" icon="lock" onClick={onCheckout}>
              Checkout securely
            </Btn>
            <div style={{ fontSize: 10.5, color: C.muted, textAlign: "center", marginTop: 8, fontWeight: 600 }}>
              MTN Mobile Money · Airtel · Card
            </div>
          </Card>

          <CheckoutTimeline />
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, valueColor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: C.muted }}>{label}</span>
      <span style={{ fontWeight: 700, color: valueColor }}>{value}</span>
    </div>
  );
}

function SellerGroup({ group, seller, onBump, onRemove }) {
  const name = seller?.store_name || group.seller_name || "Seller";
  const initial = name[0]?.toUpperCase() || "K";
  return (
    <Card pad={0}>
      <div
        style={{
          padding: "14px 18px",
          borderBottom: "1px solid rgba(196,193,183,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 10,
              background: seller?.logo_url ? `url(${seller.logo_url}) center/cover` : C.hunter,
              color: C.dust,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            {!seller?.logo_url && initial}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800 }}>{name}</div>
            <div style={{ fontSize: 10.5, color: C.muted, fontWeight: 600 }}>
              ⭐ {(seller?.rating_avg || 0).toFixed(1)}
              {seller?.address ? ` · ${seller.address}` : ""}
            </div>
          </div>
        </div>
        <button
          style={{
            border: "none",
            background: "transparent",
            color: C.hunter,
            fontSize: 11.5,
            fontWeight: 700,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontFamily: "inherit",
          }}
        >
          <Icon name="message-square" size={13} color={C.hunter} />
          Message
        </button>
      </div>
      {group.items.map((i, idx) => {
        const key = i.product_id + "|" + (i.size || "");
        const [g1, g2] = gradientForId(i.product_id);
        const img = Array.isArray(i.images) && i.images.length
          ? typeof i.images[0] === "string"
            ? i.images[0]
            : i.images[0]?.thumb || i.images[0]?.med || null
          : null;
        return (
          <div
            key={key}
            style={{
              padding: 18,
              display: "flex",
              gap: 16,
              borderBottom: idx < group.items.length - 1 ? "1px solid rgba(196,193,183,0.2)" : "none",
            }}
          >
            <div
              style={{
                width: 90,
                height: 90,
                borderRadius: 14,
                background: img ? `url(${img}) center/cover` : `linear-gradient(135deg, ${g1}, ${g2})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 40,
                flexShrink: 0,
                color: C.white,
              }}
            >
              {!img && "🛍️"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800 }}>{i.title}</div>
              {i.size && (
                <div style={{ fontSize: 11.5, color: C.muted, margin: "3px 0 6px" }}>Size {i.size}</div>
              )}
              {i.stock_qty != null && i.qty >= i.stock_qty && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "3px 8px",
                    borderRadius: 6,
                    background: C.amberBg,
                    color: C.amberDk,
                    fontSize: 10.5,
                    fontWeight: 700,
                  }}
                >
                  <Icon name="alert-triangle" size={11} color={C.amberDk} />
                  Max stock ({i.stock_qty})
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 900, color: C.hunter }}>{fmtK(i.price * i.qty)}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
                  onClick={() => onRemove(key)}
                  style={{
                    width: 28,
                    height: 28,
                    border: "1px solid rgba(196,193,183,0.4)",
                    borderRadius: 8,
                    background: C.white,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon name="trash-2" size={13} color={C.red} />
                </button>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    border: "1px solid rgba(196,193,183,0.4)",
                    borderRadius: 10,
                    overflow: "hidden",
                  }}
                >
                  <button
                    onClick={() => onBump(key, -1)}
                    style={{ width: 28, height: 28, border: "none", background: C.white, cursor: "pointer" }}
                  >
                    −
                  </button>
                  <div style={{ padding: "0 12px", fontSize: 13, fontWeight: 800 }}>{i.qty}</div>
                  <button
                    onClick={() => onBump(key, 1)}
                    style={{ width: 28, height: 28, border: "none", background: C.white, cursor: "pointer" }}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </Card>
  );
}

function CheckoutTimeline() {
  const steps = [
    ["Cart reviewed", "Now", "check", true],
    ["Address + delivery", "~30 sec", "map-pin", false],
    ["Payment · Mobile Money", "~1 min", "credit-card", false],
    ["Sellers notified", "Instant", "bell", false],
    ["First delivery window", "~15 min", "truck", false],
  ];
  return (
    <Card>
      <Eyebrow>Checkout timeline</Eyebrow>
      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 14, position: "relative" }}>
        {steps.map(([label, when, icon, done], i) => (
          <div key={label} style={{ display: "flex", gap: 12, alignItems: "flex-start", position: "relative" }}>
            {i < steps.length - 1 && (
              <div
                style={{
                  position: "absolute",
                  left: 13,
                  top: 28,
                  bottom: -14,
                  width: 2,
                  background: done ? C.hunter : C.dustDk,
                }}
              />
            )}
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: done ? C.hunter : C.white,
                border: done ? "none" : `2px solid ${C.dustDk}`,
                color: done ? C.dust : C.muted,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                zIndex: 1,
              }}
            >
              <Icon name={icon} size={13} color={done ? C.dust : C.muted} strokeWidth={2} />
            </div>
            <div style={{ flex: 1, paddingTop: 3 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: done ? C.charcoal : C.slate }}>{label}</div>
              <div style={{ fontSize: 10.5, color: C.muted, marginTop: 1 }}>{when}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
